<template>
  <div class="tl">
    <Breadcrumbs main="Турниры" title="Список турниров"/>

    <Modal
        name="DeleteTournament"
        title="Удаление турнира"
        v-model="isShowDeleteTournamentModal"
        :params="{ id: selectedTournamentId }"
    />

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar" ref="toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingTournaments }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="Поиск по ID или названию турнира…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <b-dropdown variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': !!activeGame }">
                <i class="fa fa-gamepad"></i>
                <span class="tl-drop__btn-text">{{ activeGame ? activeGame.text : 'Дисциплина' }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="setGame(null)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !!activeGame }"></i> Все дисциплины
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item v-for="game in games" :key="'g' + game.value" @click="setGame(game.value)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !isSameId(activeGameId, game.value) }"></i>
              {{ game.text }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': !!activeVariation }">
                <i class="fa fa-sliders"></i>
                <span class="tl-drop__btn-text">{{ activeVariation ? activeVariation.text : 'Тип турнира' }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="setVariation(null)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !!activeVariation }"></i> Все типы
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item v-for="v in variations" :key="'v' + v.value" @click="setVariation(v.value)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !isSameId(activeVariationId, v.value) }"></i>
              {{ v.text }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': statusRefine !== 'all' || onlyAwaited }">
                <i class="fa fa-filter"></i>
                <span class="tl-drop__btn-text">{{ statusRefine === 'all' ? 'Статус' : statusMeta(statusRefine).label }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="statusRefine = 'all'">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': statusRefine !== 'all' }"></i> Любой статус
            </b-dropdown-item>
            <b-dropdown-item v-for="kind in statusKinds" :key="'s' + kind" @click="statusRefine = kind">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': statusRefine !== kind }"></i>
              {{ statusMeta(kind).label }}
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item @click="onlyAwaited = !onlyAwaited">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !onlyAwaited }"></i> Только с заявками на модерации
            </b-dropdown-item>
          </b-dropdown>

          <div class="tl-spacer"></div>

          <div class="tl-seg" role="group" aria-label="Вид">
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'table' }"
                    @click="setViewMode('table')" v-b-tooltip.hover.bottom="'Таблица'">
              <i class="fa fa-list-ul"></i>
            </button>
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'grid' }"
                    @click="setViewMode('grid')" v-b-tooltip.hover.bottom="'Карточки'">
              <i class="fa fa-th-large"></i>
            </button>
          </div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <b-dropdown v-if="effectiveView === 'table'" variant="link" class="tl-drop tl-drop--icon" no-caret
                      right menu-class="tl-drop__menu" toggle-class="p-0">
            <template #button-content>
              <span class="tl-icon-btn" v-b-tooltip.hover.bottom="'Колонки'"><i class="fa fa-columns"></i></span>
            </template>
            <b-dropdown-form class="tl-cols">
              <div class="tl-cols__title">Колонки таблицы</div>
              <b-form-checkbox v-for="col in togglableColumns" :key="'c' + col.key"
                               :checked="visibleCols.includes(col.key)"
                               @change="toggleColumn(col.key)"
                               class="tl-cols__item">
                {{ col.label }}
              </b-form-checkbox>
              <button type="button" class="tl-cols__reset" @click="resetColumns">Сбросить</button>
            </b-dropdown-form>
          </b-dropdown>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingTournaments }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <router-link :to="{ name: 'create_tournament' }" class="tl-btn-primary">
            <i class="fa fa-plus"></i> Создать турнир
          </router-link>
        </div>

        <div v-if="hasAnyRefine" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>

          <button v-if="searchApplied" type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="activeGame" type="button" class="tl-chip" @click="setGame(null)">
            <i class="fa fa-gamepad"></i> {{ activeGame.text }} <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="activeVariation" type="button" class="tl-chip" @click="setVariation(null)">
            <i class="fa fa-sliders"></i> {{ activeVariation.text }} <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="statusRefine !== 'all'" type="button" class="tl-chip tl-chip--local" @click="statusRefine = 'all'">
            <i class="fa fa-filter"></i> {{ statusMeta(statusRefine).label }}
            <span class="tl-chip__hint">на странице</span>
            <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="onlyAwaited" type="button" class="tl-chip tl-chip--local" @click="onlyAwaited = false">
            <i class="fa fa-inbox"></i> Есть заявки на модерации
            <span class="tl-chip__hint">на странице</span>
            <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button type="button" class="tl-chips__reset" @click="resetAll">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <!-- skeleton -->
        <div v-if="isLoadingTournaments" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <!-- empty -->
        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-trophy"></i></div>
          <div class="tl-empty__title">
            {{ hasAnyRefine ? 'Ничего не нашлось' : 'Турниров пока нет' }}
          </div>
          <div class="tl-empty__text">
            {{ hasAnyRefine ? 'Попробуйте изменить условия поиска или сбросить фильтры.' : 'Создайте первый турнир — он появится в этом списке.' }}
          </div>
          <div class="tl-empty__actions">
            <button v-if="hasAnyRefine" type="button" class="tl-btn-ghost-2" @click="resetAll">Сбросить фильтры</button>
            <router-link :to="{ name: 'create_tournament' }" class="tl-btn-primary">
              <i class="fa fa-plus"></i> Создать турнир
            </router-link>
          </div>
        </div>

        <!-- table -->
        <div v-else-if="effectiveView === 'table'" class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="activeFields"
              :tbody-tr-class="rowClass"
              @row-clicked="openDetails"
          >
            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(name)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo" :style="logoStyle(item)">
                  <i v-if="!logoUrl(item)" class="fa fa-trophy"></i>
                </span>
                <router-link
                    class="tl-name__title"
                    :to="{ name: 'tournament_edit', params: { id: item.id } }" target="_blank"
                    @click.native.stop
                    :title="item.name">{{ item.name }}</router-link>
              </div>
            </template>

            <template #cell(x_kind)="{ item }">
              <span class="tl-status" :class="'tl-status--' + item.x_kindCode">
                <span class="tl-status__dot"></span>{{ statusMeta(item.x_kindCode, item).label }}
              </span>
            </template>

            <template #cell(x_format)="{ item }">
              <span class="tl-muted-strong">{{ item.x_format || '—' }}</span>
            </template>

            <template #cell(x_fill)="{ item }">
              <div class="tl-fill" v-b-tooltip.hover.top="fillTooltip(item)">
                <div class="tl-fill__top">
                  <b>{{ item.x_accepted }}</b><span class="tl-muted">/{{ item.slot_count || 0 }}</span>
                  <span class="tl-fill__pct" :class="'tl-fill--' + fillLevel(item.x_fill)">{{ item.x_fill }}%</span>
                </div>
                <div class="tl-fill__bar">
                  <div class="tl-fill__val" :class="'tl-fill--' + fillLevel(item.x_fill)"
                       :style="{ width: Math.min(item.x_fill, 100) + '%' }"></div>
                </div>
              </div>
            </template>

            <template #cell(x_awaited)="{ item }">
              <span v-if="item.x_awaited > 0" class="tl-pill tl-pill--wait"
                    v-b-tooltip.hover.top="entriesTooltip(item)">{{ item.x_awaited }}</span>
              <span v-else class="tl-muted" v-b-tooltip.hover.top="entriesTooltip(item)">—</span>
            </template>

            <template #cell(x_game)="{ item }">
              <span class="tl-muted-strong">{{ item.x_game || '—' }}</span>
            </template>

            <template #cell(x_organizer)="{ item }">
              <span class="tl-muted-strong">{{ item.x_organizer || '—' }}</span>
            </template>

            <template #cell(start_time)="{ item }">
              <div class="tl-time">
                <span class="tl-time__abs">{{ formatDate(item.start_time, item.timezone) }}</span>
                <span class="tl-time__rel" :class="relClass(item.start_time, item.timezone)">{{ formatRelative(item.start_time, item.timezone) }}</span>
              </div>
            </template>

            <template #cell(action)="{ item }">
              <div class="tl-actions" @click.stop>
                <router-link :to="{ name: 'tournament_edit', params: { id: item.id } }" target="_blank"
                             class="tl-act" v-b-tooltip.hover.top="'Редактировать'">
                  <i class="fa fa-pencil"></i>
                </router-link>
                <router-link :to="{ name: 'tournament_standings', params: { standingId: item.id } }" target="_blank"
                             class="tl-act" v-b-tooltip.hover.top="'Сетка турнира'">
                  <i class="fa fa-sitemap"></i>
                </router-link>
                <router-link :to="{ name: 'tournament_teams', params: { id: item.id } }" target="_blank"
                             class="tl-act tl-act--rel" v-b-tooltip.hover.top="'Заявки'">
                  <i class="fa fa-users"></i>
                  <span v-if="item.x_awaited > 0" class="tl-act__badge">{{ item.x_awaited > 99 ? '99+' : item.x_awaited }}</span>
                </router-link>
                <button v-if="item.x_isSquad" type="button" class="tl-act"
                        @click="calculateTeamsRatingForTournament(item.id)"
                        v-b-tooltip.hover.top="item.team_ratings_count > 0 ? 'Рейтинг рассчитан' : 'Рассчитать рейтинг'">
                  <i class="fa" :class="item.team_ratings_count > 0 ? 'fa-star tl-act--gold' : 'fa-star-o'"></i>
                </button>
                <button v-if="item.x_isSquad" type="button" class="tl-act"
                        :disabled="!(item.team_ratings_count > 0)"
                        @click="recalculateTeamsRatingForTournament(item.id)"
                        v-b-tooltip.hover.top="'Перерасчёт рейтинга'">
                  <i class="fa fa-repeat"></i>
                </button>
                <button type="button" class="tl-act tl-act--danger"
                        @click="setTournamentId(item.id)" v-b-tooltip.hover.top="'Удалить'">
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <!-- grid -->
        <div v-else class="tl-grid">
          <article v-for="item in rows" :key="'card' + item.id" class="tl-card" @click="openDetails(item)">
            <header class="tl-card__head">
              <span class="tl-card__logo" :style="logoStyle(item)">
                <i v-if="!logoUrl(item)" class="fa fa-trophy"></i>
              </span>
              <div class="tl-card__headtext">
                <router-link class="tl-card__title" :to="{ name: 'tournament_edit', params: { id: item.id } }" target="_blank"
                             @click.native.stop :title="item.name">{{ item.name }}</router-link>
                <div class="tl-card__sub">
                  <button type="button" class="tl-id tl-id--sm" @click.stop="copyId(item.id)">#{{ item.id }}<i class="fa fa-copy"></i></button>
                  <span v-if="item.x_game" class="tl-dot-sep">•</span>
                  <span v-if="item.x_game">{{ item.x_game }}</span>
                </div>
              </div>
              <span class="tl-status" :class="'tl-status--' + item.x_kindCode">
                <span class="tl-status__dot"></span>{{ statusMeta(item.x_kindCode, item).label }}
              </span>
            </header>

            <div class="tl-card__fill">
              <div class="tl-fill__top">
                <span class="tl-muted">Добор</span>
                <b>{{ item.x_accepted }}</b><span class="tl-muted">/{{ item.slot_count || 0 }}</span>
                <span class="tl-fill__pct" :class="'tl-fill--' + fillLevel(item.x_fill)">{{ item.x_fill }}%</span>
              </div>
              <div class="tl-fill__bar">
                <div class="tl-fill__val" :class="'tl-fill--' + fillLevel(item.x_fill)"
                     :style="{ width: Math.min(item.x_fill, 100) + '%' }"></div>
              </div>
            </div>

            <div class="tl-card__rows">
              <div class="tl-card__row">
                <span class="tl-muted"><i class="fa fa-clock-o"></i> Старт</span>
                <span>{{ formatDate(item.start_time, item.timezone) }} <em class="tl-time__rel" :class="relClass(item.start_time, item.timezone)">{{ formatRelative(item.start_time, item.timezone) }}</em></span>
              </div>
              <div v-if="item.x_awaited > 0" class="tl-card__row">
                <span class="tl-muted"><i class="fa fa-inbox"></i> Ожидают модерации</span>
                <span class="tl-pill tl-pill--wait">{{ item.x_awaited }}</span>
              </div>
            </div>

            <footer class="tl-card__foot" @click.stop>
              <router-link :to="{ name: 'tournament_edit', params: { id: item.id } }" target="_blank" class="tl-act" v-b-tooltip.hover.top="'Редактировать'"><i class="fa fa-pencil"></i></router-link>
              <router-link :to="{ name: 'tournament_standings', params: { standingId: item.id } }" target="_blank" class="tl-act" v-b-tooltip.hover.top="'Сетка'"><i class="fa fa-sitemap"></i></router-link>
              <router-link :to="{ name: 'tournament_teams', params: { id: item.id } }" target="_blank" class="tl-act tl-act--rel" v-b-tooltip.hover.top="'Заявки'">
                <i class="fa fa-users"></i>
                <span v-if="item.x_awaited > 0" class="tl-act__badge">{{ item.x_awaited > 99 ? '99+' : item.x_awaited }}</span>
              </router-link>
              <button v-if="item.x_isSquad" type="button" class="tl-act" @click="calculateTeamsRatingForTournament(item.id)"
                      v-b-tooltip.hover.top="item.team_ratings_count > 0 ? 'Рейтинг рассчитан' : 'Рассчитать рейтинг'">
                <i class="fa" :class="item.team_ratings_count > 0 ? 'fa-star tl-act--gold' : 'fa-star-o'"></i>
              </button>
              <button v-if="item.x_isSquad" type="button" class="tl-act" :disabled="!(item.team_ratings_count > 0)"
                      @click="recalculateTeamsRatingForTournament(item.id)" v-b-tooltip.hover.top="'Перерасчёт рейтинга'">
                <i class="fa fa-repeat"></i>
              </button>
              <span class="tl-spacer"></span>
              <button type="button" class="tl-act tl-act--danger" @click="setTournamentId(item.id)" v-b-tooltip.hover.top="'Удалить'">
                <i class="fa fa-trash-o"></i>
              </button>
            </footer>
          </article>
        </div>

        <!-- footer / pagination -->
        <div v-if="!isLoadingTournaments && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ total || 0 }}</b>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="currentPageModel"
              :total-rows="total"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>

    <!-- ================== DETAILS DRAWER ================== -->
    <transition name="tl-fade">
      <div v-if="details" class="tl-backdrop" @click="details = null"></div>
    </transition>
    <transition name="tl-slide">
      <aside v-if="details" class="tl-drawer">
        <header class="tl-drawer__head">
          <span class="tl-card__logo" :style="logoStyle(details)">
            <i v-if="!logoUrl(details)" class="fa fa-trophy"></i>
          </span>
          <div class="tl-drawer__headtext">
            <div class="tl-drawer__title">{{ details.name }}</div>
            <div class="tl-card__sub">
              <button type="button" class="tl-id tl-id--sm" @click="copyId(details.id)">#{{ details.id }}<i class="fa fa-copy"></i></button>
              <span class="tl-status" :class="'tl-status--' + details.x_kindCode">
                <span class="tl-status__dot"></span>{{ statusMeta(details.x_kindCode, details).label }}
              </span>
            </div>
          </div>
          <button type="button" class="tl-icon-btn" @click="details = null"><i class="fa fa-times"></i></button>
        </header>

        <div class="tl-drawer__body">
          <div class="tl-fill tl-fill--lg">
            <div class="tl-fill__top">
              <span class="tl-muted">Заполнение слотов</span>
              <b>{{ details.x_accepted }}</b><span class="tl-muted">/{{ details.slot_count || 0 }}</span>
              <span class="tl-fill__pct" :class="'tl-fill--' + fillLevel(details.x_fill)">{{ details.x_fill }}%</span>
            </div>
            <div class="tl-fill__bar">
              <div class="tl-fill__val" :class="'tl-fill--' + fillLevel(details.x_fill)"
                   :style="{ width: Math.min(details.x_fill, 100) + '%' }"></div>
            </div>
          </div>

          <div class="tl-drawer__stats">
            <div class="tl-dstat tl-dstat--ok"><span>{{ details.x_accepted }}</span><small>принято</small></div>
            <div class="tl-dstat tl-dstat--wait"><span>{{ details.x_awaited }}</span><small>ожидают</small></div>
            <div class="tl-dstat tl-dstat--no"><span>{{ details.x_rejected }}</span><small>отклонено</small></div>
          </div>

          <dl class="tl-dl">
            <div><dt>Игра</dt><dd>{{ details.x_game || '—' }}</dd></div>
            <div><dt>Формат</dt><dd>{{ details.x_format || '—' }}</dd></div>
            <div><dt>Тип</dt><dd>{{ details.x_variation || '—' }}</dd></div>
            <div><dt>Организатор</dt><dd>{{ details.x_organizer || '—' }}</dd></div>
            <div><dt>Старт турнира</dt><dd>{{ formatDate(details.start_time, details.timezone) }} <em class="tl-time__rel" :class="relClass(details.start_time, details.timezone)">{{ formatRelative(details.start_time, details.timezone) }}</em></dd></div>
            <div v-if="details.start_reg_time"><dt>Начало регистрации</dt><dd>{{ formatDate(details.start_reg_time, details.timezone) }}</dd></div>
            <div v-if="details.end_reg_time"><dt>Конец регистрации</dt><dd>{{ formatDate(details.end_reg_time, details.timezone) }}</dd></div>
            <div v-if="details.prize_fond"><dt>Призовой фонд</dt><dd>{{ details.prize_fond }}</dd></div>
            <div v-if="details.slug"><dt>Slug</dt><dd class="tl-mono">{{ details.slug }}</dd></div>
          </dl>
        </div>

        <footer class="tl-drawer__foot">
          <router-link :to="{ name: 'tournament_edit', params: { id: details.id } }" target="_blank" class="tl-btn-primary">
            <i class="fa fa-pencil"></i> Редактировать
          </router-link>
          <router-link :to="{ name: 'tournament_standings', params: { standingId: details.id } }" target="_blank" class="tl-btn-ghost-2">
            <i class="fa fa-sitemap"></i> Сетка
          </router-link>
          <router-link :to="{ name: 'tournament_teams', params: { id: details.id } }" target="_blank" class="tl-btn-ghost-2">
            <i class="fa fa-users"></i> Заявки
            <span v-if="details.x_awaited > 0" class="tl-act__badge tl-act__badge--static">{{ details.x_awaited }}</span>
          </router-link>
        </footer>
      </aside>
    </transition>
  </div>
</template>

<script>
import {TournamentAdapterMixin} from "../../mixins/Tournament/TournamentAdapterMixin";
import {ModalAdapterMixin} from "@/mixins/modal/ModalAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import Modal from "@/components/modals/index.vue";
import {
  formatTournamentDateTime,
  formatTournamentRelative,
  tournamentTimeDiff,
} from "@/common/tournamentTime";
import api from "@/api/api";
import Vue from "vue";

const STATUS_META = {
  pending: {label: "Регистрация скоро", rank: 0},
  registration: {label: "Идёт регистрация", rank: 1},
  game: {label: "Идёт игра", rank: 2},
  ended: {label: "Завершён", rank: 3},
  unknown: {label: "—", rank: 4},
};

const LS = {
  view: "tournamentsListView",
  density: "tournamentsListDensity",
  columns: "tournamentsListColumns",
};

const ALL_COLUMNS = [
  {key: "id", label: "ID", sortable: true, thClass: "tl-th tl-th--id", tdClass: "tl-td"},
  {key: "name", label: "Турнир", sortable: true, thClass: "tl-th tl-th--name", tdClass: "tl-td", locked: true},
  {key: "x_kind", label: "Статус", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "x_fill", label: "Добор", sortable: true, thClass: "tl-th tl-th--fill", tdClass: "tl-td"},
  {key: "x_awaited", label: "Ожидают", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "x_format", label: "Формат", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "x_game", label: "Игра", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "x_organizer", label: "Организатор", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "start_time", label: "Старт турнира", sortable: true, thClass: "tl-th", tdClass: "tl-td"},
  {key: "action", label: "", sortable: false, thClass: "tl-th tl-th--act", tdClass: "tl-td tl-td--act", locked: true},
];

const HIDDEN_BY_DEFAULT = ["x_format", "x_organizer"];
const DEFAULT_COLUMNS = ALL_COLUMNS
    .filter((c) => !c.locked && !HIDDEN_BY_DEFAULT.includes(c.key))
    .map((c) => c.key);

const STATUS_KINDS = ["registration", "game", "pending", "ended"];

export default {
  name: "TournamentsList",
  components: {Modal},
  mixins: [TournamentAdapterMixin, ModalAdapterMixin, PaginationHelperMixin],

  data() {
    return {
      pageKey: "pageNumberTournamentsList",
      perPage: 10,

      searchModel: "",
      searchTimer: null,

      statusRefine: "all",
      onlyAwaited: false,
      statusKinds: STATUS_KINDS,

      viewMode: "table",
      density: "comfortable",
      visibleCols: DEFAULT_COLUMNS.slice(),

      details: null,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1440,
      stickyFrame: null,

      isShowDeleteTournamentModal: false,
      selectedTournamentId: null,
    };
  },

  computed: {
    isDev() {
      return process.env.NODE_ENV === "development";
    },

    activeGameId() {
      return this.$route.query.game || null;
    },
    activeVariationId() {
      return this.$route.query.variation_id || null;
    },
    searchApplied() {
      return this.$route.query.search || "";
    },
    activeGame() {
      if (!this.activeGameId) return null;
      return (this.games || []).find((g) => this.isSameId(g.value, this.activeGameId)) ||
          {value: this.activeGameId, text: "Игра #" + this.activeGameId};
    },
    activeVariation() {
      if (!this.activeVariationId) return null;
      return (this.variations || []).find((v) => this.isSameId(v.value, this.activeVariationId)) ||
          {value: this.activeVariationId, text: "Тип #" + this.activeVariationId};
    },

    hasActiveFilters() {
      return !!(this.activeGameId || this.activeVariationId || this.searchApplied);
    },
    hasAnyRefine() {
      return this.hasActiveFilters || this.statusRefine !== "all" || this.onlyAwaited;
    },

    /** Сырые турниры текущей страницы, обогащённые вычисленными полями (x_*) */
    pageItems() {
      const list = Array.isArray(this.tournaments) ? this.tournaments : [];
      return list.map((t) => {
        const accepted = Number(t.accepted_entries_count) || 0;
        const awaited = Number(t.awaited_entries_count) || 0;
        const rejected = Number(t.rejected_entries_count) || 0;
        const kindCode = this.statusKind(t);
        const formatName = (t.format && t.format.name) || "";
        return Object.assign({}, t, {
          x_accepted: accepted,
          x_awaited: awaited,
          x_rejected: rejected,
          x_fill: this.fillPercent(t.slot_count, accepted),
          x_kindCode: kindCode,
          x_kind: STATUS_META[kindCode].rank,
          x_format: formatName,
          x_isSquad: String(formatName).toLowerCase() === "squad",
          x_game: (t.game && t.game.name) || "",
          x_organizer: (t.organizer && t.organizer.name) || "",
          x_variation: (t.variation && (t.variation.label || t.variation.name)) || "",
        });
      });
    },

    /** То, что реально рисуем — с учётом локальных уточнений по странице */
    rows() {
      let list = this.pageItems;
      if (this.statusRefine !== "all") list = list.filter((t) => t.x_kindCode === this.statusRefine);
      if (this.onlyAwaited) list = list.filter((t) => t.x_awaited > 0);
      return list;
    },

    togglableColumns() {
      return ALL_COLUMNS.filter((c) => !c.locked);
    },
    activeFields() {
      return ALL_COLUMNS.filter((c) => c.locked || this.visibleCols.includes(c.key));
    },

    effectiveView() {
      return this.windowWidth < 992 ? "grid" : this.viewMode;
    },

    currentPageModel: {
      get() {
        return Number(this.currentPage) || 1;
      },
      set(value) {
        this.currentPage = Number(value) || 1;
      },
    },
    rangeFrom() {
      if (!this.pageItems.length) return 0;
      return (this.currentPageModel - 1) * this.perPage + 1;
    },
    rangeTo() {
      return (this.currentPageModel - 1) * this.perPage + this.pageItems.length;
    },
  },

  watch: {
    "$route.query": {
      deep: true,
      handler(newVal, oldVal) {
        // не реагируем на смену query при уходе на другую страницу
        if (this.$route.name !== "tournaments") return;
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
        if ((newVal.search || "") !== this.searchModel) this.searchModel = newVal.search || "";
        this.fetchTournaments();
      },
    },
    searchModel(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if ((value || "") === this.searchApplied) return;
        this.applyQuery({search: value || undefined, page: 1});
      }, 400);
    },
    details(value) {
      if (typeof document !== "undefined") {
        document.body.style.overflow = value ? "hidden" : "";
      }
    },
  },

  created() {
    this.searchModel = this.$route.query.search || "";
    this.restorePreferences();
  },

  async mounted() {
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.updateStickyOffsets, {passive: true});
    this.$nextTick(this.updateStickyOffsets);

    localStorage.removeItem("selectedStageId");
    localStorage.removeItem("selectedGroupId");

    await this.fetchTournaments();
    this.$nextTick(this.updateStickyOffsets);

    // Справочники нужны формам создания/редактирования — грузим фоном
    Promise.all([
      this.indexFormat({}),
      this.indexTimeZones(),
      this.getCountriesQuery(),
      this.grabGames(),
      this.grabVariations(),
    ]).catch((e) => console.error("Не удалось загрузить справочники:", e));
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("scroll", this.updateStickyOffsets);
    clearTimeout(this.searchTimer);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    if (typeof document !== "undefined") document.body.style.overflow = "";
  },

  methods: {
    // ----------------------------- данные -----------------------------
    async fetchTournaments(page) {
      const query = this.$route.query;
      const pageNumber = Number(page || query.page || this.getCurrentPageNumber(this.pageKey) || 1) || 1;

      this.isLoadingTournaments = true;
      try {
        await this.indexTournaments({
          pageNumber,
          ...(query.game && {gameId: query.game}),
          ...(query.variation_id && {variation_id: query.variation_id}),
          ...(query.search && {search: query.search}),
        });
        this.recordPageToStorage(pageNumber, this.pageKey);
        this.selectedGameId = query.game ? Number(query.game) : null;
      } catch (e) {
        Vue.toasted.error("Не удалось загрузить турниры", {position: "top-right", duration: 3000});
      } finally {
        this.isLoadingTournaments = false;
      }
    },

    refresh() {
      this.fetchTournaments();
    },

    // ----------------------------- фильтры -----------------------------
    applyQuery(patch) {
      const next = Object.assign({}, this.$route.query, patch);
      Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === null || next[key] === "") delete next[key];
      });
      if (JSON.stringify(next) === JSON.stringify(this.$route.query)) return;
      this.$router.push({path: this.$route.path, query: next}).catch(() => {});
    },

    setGame(value) {
      this.applyQuery({game: value === null ? undefined : value, page: 1});
    },

    setVariation(value) {
      this.applyQuery({variation_id: value === null ? undefined : value, page: 1});
    },

    clearSearch() {
      this.searchModel = "";
      this.applyQuery({search: undefined, page: 1});
    },

    resetAll() {
      this.searchModel = "";
      this.statusRefine = "all";
      this.onlyAwaited = false;
      if (Object.keys(this.$route.query).length) {
        this.$router.push({path: this.$route.path}).catch(() => {});
      } else {
        this.fetchTournaments(1);
      }
    },

    goToPage(page) {
      this.applyQuery({page});
      // если query не изменился (например, ручной клик по текущей странице) — ничего не делаем
    },

    // ----------------------------- настройки вида -----------------------------
    restorePreferences() {
      try {
        const view = localStorage.getItem(LS.view);
        if (view === "table" || view === "grid") this.viewMode = view;

        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "comfortable") this.density = density;

        const columns = JSON.parse(localStorage.getItem(LS.columns) || "null");
        if (Array.isArray(columns)) this.visibleCols = columns;
      } catch (e) {
        /* настройки не критичны */
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem(LS.view, mode);
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "comfortable" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    toggleColumn(key) {
      const selected = new Set(this.visibleCols);
      if (selected.has(key)) selected.delete(key);
      else selected.add(key);
      // сохраняем исходный порядок колонок
      this.visibleCols = ALL_COLUMNS.filter((c) => !c.locked && selected.has(c.key)).map((c) => c.key);
      localStorage.setItem(LS.columns, JSON.stringify(this.visibleCols));
    },

    resetColumns() {
      this.visibleCols = DEFAULT_COLUMNS.slice();
      localStorage.setItem(LS.columns, JSON.stringify(this.visibleCols));
    },

    // ----------------------------- отображение -----------------------------
    isSameId(a, b) {
      return a !== null && a !== undefined && String(a) === String(b);
    },

    statusKind(item) {
      const raw = String((item && item.status && (item.status.code || item.status.name)) || item.status || "").toLowerCase();
      if (!raw) return "unknown";
      if (/not[\s_-]*start/.test(raw) || /soon/.test(raw)) return "pending";
      if (/reg/.test(raw)) return "registration";
      if (/game|progress|live|play|process/.test(raw)) return "game";
      if (/end|finish|complet|done|over/.test(raw)) return "ended";
      return "unknown";
    },

    statusMeta(code, item) {
      const meta = STATUS_META[code] || STATUS_META.unknown;
      if (code === "unknown" && item) {
        const raw = (item.status && (item.status.code || item.status.name)) || "";
        if (raw) return {label: String(raw), rank: meta.rank};
      }
      return meta;
    },

    fillPercent(slotCount, accepted) {
      const slots = Number(slotCount) || 0;
      if (!slots) return 0;
      return Math.round(((Number(accepted) || 0) / slots) * 100);
    },

    // Подсвечиваем только то, что требует внимания: слабо заполненные турниры
    fillLevel(percent) {
      return percent < 35 ? "low" : "normal";
    },

    fillTooltip(item) {
      return `Принято ${item.x_accepted} из ${item.slot_count || 0} слотов`;
    },

    entriesTooltip(item) {
      return `Принято: ${item.x_accepted} · Ожидают: ${item.x_awaited} · Отклонено: ${item.x_rejected}`;
    },

    logoUrl(item) {
      if (!item) return null;
      return (item.logo && item.logo.original_url) ||
          (item.mobile_logo && item.mobile_logo.original_url) ||
          item.image ||
          null;
    },

    logoStyle(item) {
      const url = this.logoUrl(item);
      return url ? {backgroundImage: `url(${url})`} : {};
    },

    /** Время турнира — стенное в его зоне; показываем в локальной зоне админа. */
    formatDate(value, timezone) {
      return formatTournamentDateTime(value, timezone);
    },

    formatRelative(value, timezone) {
      return formatTournamentRelative(value, timezone);
    },

    relClass(value, timezone) {
      const diff = tournamentTimeDiff(value, timezone);
      if (diff === null) return "";
      if (diff < 0) return "is-past";
      if (diff < 86400000) return "is-soon";
      return "";
    },

    rowClass(item, type) {
      if (type !== "row" || !item) return null;
      return ["tl-row", "tl-row--" + item.x_kindCode, item.x_awaited > 0 ? "tl-row--awaited" : ""];
    },

    openDetails(item) {
      this.details = item;
    },

    async copyId(id) {
      const text = String(id);
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const input = document.createElement("textarea");
          input.value = text;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        Vue.toasted.success(`ID ${text} скопирован`, {position: "top-right", duration: 1500});
      } catch (e) {
        Vue.toasted.error("Не удалось скопировать", {position: "top-right", duration: 2000});
      }
    },

    // ----------------------------- действия -----------------------------
    setTournamentId(id) {
      this.selectedTournamentId = id;
      this.isShowDeleteTournamentModal = true;
    },

    async recalculateTeamsRatingForTournament(tournamentId) {
      try {
        await api.tournament.recalculateRating(tournamentId);
        Vue.toasted.success("Перерасчет запущен", {position: "top-right", duration: 3000});
      } catch (error) {
        Vue.toasted.error(
            (error.response && error.response.data && error.response.data.message) || "Перерасчет запустить не удалось",
            {position: "top-right", duration: 3000}
        );
      }
    },

    // ----------------------------- горячие клавиши -----------------------------
    onResize() {
      this.windowWidth = window.innerWidth;
      this.updateStickyOffsets();
    },

    /**
     * Шапка приложения зафиксирована и её высота зависит от темы/разрешения,
     * поэтому липкие оффсеты тулбара и заголовков таблицы считаем по факту.
     */
    updateStickyOffsets() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        if (!this.$el || !this.$el.style) return;

        const appHeader = document.querySelector(".page-header");
        const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;

        this.$el.style.setProperty("--tl-sticky-top", headerHeight + "px");
      });
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (this.details) {
          this.details = null;
          return;
        }
        if (isTyping) return;
        if (this.hasAnyRefine) this.resetAll();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
        return;
      }
      if (event.key === "n" || event.key === "N" || event.key === "т" || event.key === "Т") {
        event.preventDefault();
        this.$router.push({name: "create_tournament"}).catch(() => {});
        return;
      }
      if (event.key === "ArrowLeft" && this.currentPageModel > 1) {
        this.goToPage(this.currentPageModel - 1);
        return;
      }
      if (event.key === "ArrowRight" && this.currentPageModel * this.perPage < (this.total || 0)) {
        this.goToPage(this.currentPageModel + 1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
</style>
